/**
 * Add to Cart Button Component
 *
 * @package Enterprise\BookingSystem\Blocks
 */

/**
 * WordPress dependencies
 */
import { useDispatch, useSelect } from '@wordpress/data';
import { __ } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
import { store as addToCartStore } from '../../../data/add-to-cart';

type AddToCartButtonProps = {
	readonly productId: number;
	readonly productType: string;
};

export const AddToCartButton = ({ productId, productType }: AddToCartButtonProps) => {
	const { error, isAdding, quantity, selectedVariantId, validationErrors } =
		useSelect(
			(select) => {
				const storeSelectors = select(addToCartStore);
				return {
					error: storeSelectors.getError(productId),
					isAdding: storeSelectors.isAdding(productId),
					quantity: storeSelectors.getQuantity(productId),
					selectedVariantId: storeSelectors.getSelectedVariantId(productId),
					validationErrors: storeSelectors.getValidationErrors(productId),
				};
			},
			[productId],
		);

	const dispatch = useDispatch(addToCartStore);

	const isVariableProduct = productType === 'variable';
	const hasRequiredVariant = isVariableProduct ? selectedVariantId !== null : true;

	const canSubmit =
		!isAdding &&
		hasRequiredVariant &&
		Object.keys(validationErrors).length === 0;

	const cartCommand = {
		productId,
		product_id: productId,
		quantity,
		variant_id: selectedVariantId ?? undefined,
	};

	const handleClick = () => {
		if (!canSubmit) return;
		void dispatch.addToCart(cartCommand);
	};

	const handleBuyNow = async () => {
		if (!canSubmit) return;
		try {
			await dispatch.addToCart(cartCommand);
			globalThis.location.href = '/checkout';
		} catch {
			// error is handled in the store
		}
	};

	return (
		<div className="ebs-add-to-cart__actions">
			{error ? (
				<div className="ebs-add-to-cart__error-banner" role="alert" aria-live="assertive">
					<span className="ebs-add-to-cart__error-icon" aria-hidden="true">
						⚠️
					</span>
					<span>{error}</span>
				</div>
			) : null}

			<div className="ebs-add-to-cart__buttons-row">
				<button
					type="button"
					className="ebs-add-to-cart__button ebs-add-to-cart__button--secondary"
					onClick={ () => { void handleBuyNow(); } }
					disabled={!canSubmit || isAdding}
					aria-busy={isAdding}
				>
					{__('Buy Now', 'enterprise-booking-system')}
				</button>

				<button
					type="button"
					className="ebs-add-to-cart__button ebs-add-to-cart__button--primary"
					onClick={handleClick}
					disabled={!canSubmit || isAdding}
					aria-busy={isAdding}
				>
					{isAdding ? (
						<>
							<span className="ebs-add-to-cart__spinner" aria-hidden="true" />
							<span>{__('Adding...', 'enterprise-booking-system')}</span>
						</>
					) : (
						__('Add to Cart', 'enterprise-booking-system')
					)}
				</button>
			</div>

			</div>
	);
};
